import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { useMandalName, useDistribution, refreshMandalName, slugifyName } from '../hooks/useMandalName';
import { refreshFeatures } from '../hooks/useFeatures';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Paper,
  Alert,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  AdminPanelSettings,
  PersonAdd,
  Settings,
  Backup,
  RestoreFromTrash,
  Notifications,
  Security,
  Analytics,
  Payment,
  Warning,
  Group,
  Event,
  ExpandMore,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Lock,
  Visibility,
  Download,
  PictureAsPdf,
  Upload,
  Schedule,
  AccountBalance,
  TrendingUp,
  Assignment,
  CurrencyRupee,
  Gavel,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { formatINR } from '../utils/currency';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import { ACCENTS, tint } from '../theme/accents';

// Configure dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Kolkata');

const FEATURE_LABELS = {
  featureLoans: 'Loans',
  featureExpenses: 'Expenses',
  featurePenalties: 'Penalties',
  featureMeetings: 'Meetings',
  featureSilverDistribution: 'Distribution',
};

const AdminPanel = () => {
  const { user } = useAuth();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const mandalName = useMandalName();
  const { title: distributionTitle } = useDistribution();
  // The draw's label is the group's own (Silver, Gold, Cash...), so it is not a constant
  const featureLabels = { ...FEATURE_LABELS, featureSilverDistribution: distributionTitle };
  const [systemSettings, setSystemSettings] = useState({
    mandalName: '',
    distributionName: 'Silver',
    monthlyAmount: 5000,
    penaltyAmount: 500,
    meetingDay: 5, // Friday
    meetingTime: '22:00',
    silverDistributionMonths: 2,
    autoNotifications: true,
    latePenaltyDays: 5,
  });
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    fullName: '',
    email: '',
    phone: '',
    password: '',
    isAdmin: false,
  });
  const [selectedAction, setSelectedAction] = useState('');
  const [features, setFeatures] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentActivity();
    fetchSystemSettings();
    fetchPendingActions();
    fetchSchedulerStatus();
    fetchFeatures();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/api/admin/dashboard');
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats');
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await axios.get('/api/admin/recent-activity');
      setRecentActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch recent activity');
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get('/api/admin/settings');
      if (response.data) {
        setSystemSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch system settings');
    }
  };

  const fetchPendingActions = async () => {
    try {
      const [paymentsRes, penaltiesRes, meetingsRes] = await Promise.all([
        axios.get('/api/payments?status=pending'),
        axios.get('/api/penalties?status=pending'),
        axios.get('/api/meetings?status=scheduled'),
      ]);
      
      const actions = [
        ...paymentsRes.data.slice(0, 5).map(p => ({
          type: 'payment',
          title: `Payment Review: ${p.user_name}`,
          subtitle: `${formatINR(p.amount)} — ${p.payment_month}`,
          date: p.payment_date,
          id: p.id,
        })),
        ...penaltiesRes.data.slice(0, 5).map(p => ({
          type: 'penalty',
          title: `Penalty: ${p.user_name}`,
          subtitle: `${formatINR(p.amount)} — ${p.reason}`,
          date: p.penalty_date,
          id: p.id,
        })),
        ...meetingsRes.data.slice(0, 3).map(m => ({
          type: 'meeting',
          title: `Upcoming Meeting`,
          subtitle: dayjs(m.meeting_date).format('MMM D, YYYY'),
          date: m.meeting_date,
          id: m.id,
        })),
      ].sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setPendingActions(actions);
    } catch (error) {
      console.error('Failed to fetch pending actions');
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get('/api/admin/scheduler/status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status');
    }
  };

  const handleCreateUser = async () => {
    try {
      await axios.post('/api/users', newUserForm);
      toast.success('User created successfully');
      setNewUserDialogOpen(false);
      setNewUserForm({
        username: '',
        fullName: '',
        email: '',
        phone: '',
        password: '',
        isAdmin: false,
      });
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  // Feature switches. Turning one off hides that section for everyone; the data
  // stays untouched, so switching it back on restores the history intact.
  const fetchFeatures = async () => {
    try {
      const response = await axios.get('/api/rules/features');
      setFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch features');
    }
  };

  const handleToggleFeature = async (key, value) => {
    const previous = features;
    setFeatures({ ...features, [key]: value });
    try {
      await axios.put('/api/rules/features', { [key]: value });
      // Let the sidebar and pages follow straight away
      refreshFeatures();
      toast.success(`${featureLabels[key]} ${value ? 'enabled' : 'hidden'}`);
    } catch (error) {
      setFeatures(previous);
      toast.error('Could not change that setting');
    }
  };

  const handleUpdateSettings = async () => {
    if (!String(systemSettings.mandalName || '').trim()) {
      toast.error("Enter your mandal's name");
      return;
    }
    if (!String(systemSettings.distributionName || '').trim()) {
      toast.error('Enter what your group distributes, for example Silver, Gold or Cash');
      return;
    }

    try {
      await axios.put('/api/admin/settings', systemSettings);
      // Show the new name in the header straight away
      await refreshMandalName();
      toast.success('System settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    }
  };

  const handleBulkAction = async () => {
    if (!selectedAction) {
      toast.error('Please select an action');
      return;
    }

    try {
      switch (selectedAction) {
        case 'generate-penalties':
          await axios.post('/api/admin/bulk/generate-penalties');
          toast.success('Late penalties generated for all overdue payments');
          break;
        case 'send-reminders':
          await axios.post('/api/admin/bulk/send-reminders');
          toast.success('Payment reminders sent to all pending members');
          break;
        case 'monthly-setup':
          await axios.post('/api/admin/bulk/setup-monthly-payments');
          toast.success('Monthly payment records created for all members');
          break;
        case 'backup-data':
          await axios.post('/api/admin/backup');
          toast.success('System backup created successfully');
          break;
        default:
          toast.error('Unknown action');
      }
      setBulkActionDialogOpen(false);
      fetchDashboardStats();
      fetchPendingActions();
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const handleExportData = async (type) => {
    try {
      const response = await axios.get(`/api/admin/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-${dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD_HH-mm-ss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${type} data exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${type} data`);
    }
  };

  const handleDownloadReport = async () => {
    setReportLoading(true);
    try {
      const response = await axios.get('/api/admin/reports/full', { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${slugifyName(mandalName)}-full-report-${dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Full report downloaded');
    } catch (error) {
      toast.error('Failed to generate the report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleSchedulerTask = async (taskType) => {
    try {
      const response = await axios.post(`/api/admin/scheduler/run-${taskType}`);
      toast.success(response.data.message);
      fetchDashboardStats();
      fetchPendingActions();
    } catch (error) {
      toast.error(`Failed to run ${taskType}`);
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'payment': return <Payment />;
      case 'penalty': return <Warning />;
      case 'meeting': return <Event />;
      default: return <Assignment />;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'payment': return 'primary';
      case 'penalty': return 'warning';
      case 'meeting': return 'success';
      default: return 'default';
    }
  };

  if (!user?.isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Access Denied: You don't have admin privileges to access this panel.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Administration"
        accent={ACCENTS.gold}
        title="Admin Panel"
        subtitle="Members, bulk operations, scheduled jobs and the business rules the whole app reads from."
      />

      <Alert severity="info" icon={<AdminPanelSettings />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Administrator Dashboard:</strong> Centralized control panel for system management, 
          bulk operations, and administrative tasks.
        </Typography>
      </Alert>

      {/* Quick Stats */}
      {dashboardStats && (
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={6} md={3}>
            <StatCard title="Members" value={dashboardStats.totalUsers || 0}
              subtitle="On the roster" icon={<Group />} accent={ACCENTS.indigo} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Collected" value={formatINR(dashboardStats.totalCollected || 0)}
              subtitle="All time" icon={<CurrencyRupee />} accent={ACCENTS.emerald} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Pending" value={dashboardStats.pendingPayments || 0}
              subtitle="Payments due" icon={<Warning />} accent={ACCENTS.marigold} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Penalties" value={dashboardStats.pendingPenalties || 0}
              subtitle="Unsettled" icon={<Gavel />} accent={ACCENTS.vermilion} />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAdd />}
                    onClick={() => setNewUserDialogOpen(true)}
                    sx={{ mb: 1 }}
                  >
                    Add New User
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Assignment />}
                    onClick={() => setBulkActionDialogOpen(true)}
                    sx={{ mb: 1 }}
                  >
                    Bulk Actions
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Schedule />}
                    onClick={() => handleBulkAction('monthly-setup')}
                    sx={{ mb: 1 }}
                  >
                    Setup Monthly
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Backup />}
                    onClick={() => setBackupDialogOpen(true)}
                    sx={{ mb: 1, whiteSpace: 'nowrap' }}
                  >
                    Backup
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Actions ({pendingActions.length})
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {pendingActions.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No pending actions
                  </Typography>
                ) : (
                  pendingActions.map((action, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: `${getActionColor(action.type)}.main`, width: 32, height: 32 }}>
                          {getActionIcon(action.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={action.title}
                        secondary={action.subtitle}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Scheduler Status & Control */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Automated Tasks & Scheduler
              </Typography>
              
              {schedulerStatus ? (
                <Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Chip 
                      label={schedulerStatus.running ? 'Running' : 'Stopped'}
                      color={schedulerStatus.running ? 'success' : 'error'}
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {schedulerStatus.message}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Manual Task Execution:
                  </Typography>
                  <Grid container spacing={2.5} mb={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Schedule />}
                        onClick={() => handleSchedulerTask('monthly-setup')}
                        sx={{ mb: 1 }}
                      >
                        Setup Monthly Payments
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Warning />}
                        onClick={() => handleSchedulerTask('penalty-generation')}
                        sx={{ mb: 1 }}
                      >
                        Generate Penalties
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Notifications />}
                        onClick={() => handleSchedulerTask('reminders')}
                        sx={{ mb: 1 }}
                      >
                        Send Reminders
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Analytics />}
                        onClick={fetchSchedulerStatus}
                        sx={{ mb: 1 }}
                      >
                        Refresh Status
                      </Button>
                    </Grid>
                  </Grid>
                  
                  {schedulerStatus.tasks && schedulerStatus.tasks.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Scheduled Tasks:
                      </Typography>
                      <List dense>
                        {schedulerStatus.tasks.map((task, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <Avatar sx={{ width: 26, height: 26, bgcolor: (t) => tint(task.isRunning ? ACCENTS.emerald : ACCENTS.vermilion, 0.18), color: task.isRunning ? ACCENTS.emerald : ACCENTS.vermilion }}>
                                <CheckCircle fontSize="small" />
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={task.name}
                              secondary={task.description || task.schedule}
                              primaryTypographyProps={{ fontWeight: 600 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Loading scheduler status...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Feature switches */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Features
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Hide sections the group does not use. Nothing is deleted — switch one back on
                and its records are exactly as they were.
              </Typography>
              <Grid container spacing={1}>
                {features && Object.keys(featureLabels).map((key) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(features[key])}
                          onChange={(e) => handleToggleFeature(key, e.target.checked)}
                        />
                      }
                      label={featureLabels[key]}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* System Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Settings
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    fullWidth
                    label="Mandal name"
                    value={systemSettings.mandalName || ''}
                    onChange={(e) => setSystemSettings({ ...systemSettings, mandalName: e.target.value })}
                    inputProps={{ maxLength: 60 }}
                    helperText="Shown in the header, on the sign-in page, in reports and in WhatsApp messages."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Monthly Amount"
                    type="number"
                    value={systemSettings.monthlyAmount}
                    onChange={(e) => setSystemSettings({ ...systemSettings, monthlyAmount: e.target.value })}
                    InputProps={{ startAdornment: '₹' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Default Penalty"
                    type="number"
                    value={systemSettings.penaltyAmount}
                    onChange={(e) => setSystemSettings({ ...systemSettings, penaltyAmount: e.target.value })}
                    InputProps={{ startAdornment: '₹' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Meeting Day"
                    value={systemSettings.meetingDay}
                    onChange={(e) => setSystemSettings({ ...systemSettings, meetingDay: e.target.value })}
                  >
                    <MenuItem value={0}>Sunday</MenuItem>
                    <MenuItem value={1}>Monday</MenuItem>
                    <MenuItem value={2}>Tuesday</MenuItem>
                    <MenuItem value={3}>Wednesday</MenuItem>
                    <MenuItem value={4}>Thursday</MenuItem>
                    <MenuItem value={5}>Friday</MenuItem>
                    <MenuItem value={6}>Saturday</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Meeting Time"
                    type="time"
                    value={systemSettings.meetingTime}
                    onChange={(e) => setSystemSettings({ ...systemSettings, meetingTime: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="What each draw hands out"
                    value={systemSettings.distributionName || ''}
                    onChange={(e) => setSystemSettings({ ...systemSettings, distributionName: e.target.value })}
                    inputProps={{ maxLength: 30 }}
                    helperText="Silver, Gold, Cash… It names the draw everywhere, e.g. “Gold Distribution”. If your group holds no draw, switch it off under Features."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Distribution every (months)"
                    type="number"
                    value={systemSettings.silverDistributionMonths}
                    onChange={(e) => setSystemSettings({ ...systemSettings, silverDistributionMonths: e.target.value })}
                    helperText="0 means no fixed schedule."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Late Penalty Days"
                    type="number"
                    value={systemSettings.latePenaltyDays}
                    onChange={(e) => setSystemSettings({ ...systemSettings, latePenaltyDays: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={systemSettings.autoNotifications}
                        onChange={(e) => setSystemSettings({ ...systemSettings, autoNotifications: e.target.checked })}
                      />
                    }
                    label="Auto Notifications"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleUpdateSettings}
                    startIcon={<Settings />}
                  >
                    Update Settings
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create User Dialog */}
      <Dialog open={newUserDialogOpen} onClose={() => setNewUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={newUserForm.username}
                onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={newUserForm.fullName}
                onChange={(e) => setNewUserForm({ ...newUserForm, fullName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={newUserForm.phone}
                onChange={(e) => setNewUserForm({ ...newUserForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                required
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUserForm.isAdmin}
                    onChange={(e) => setNewUserForm({ ...newUserForm, isAdmin: e.target.checked })}
                  />
                }
                label="Admin Privileges"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Actions Dialog */}
      <Dialog open={bulkActionDialogOpen} onClose={() => setBulkActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Operations</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select Action"
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            sx={{ mt: 2 }}
          >
            <MenuItem value="generate-penalties">Generate Late Penalties</MenuItem>
            <MenuItem value="send-reminders">Send Payment Reminders</MenuItem>
            <MenuItem value="monthly-setup">Setup Monthly Payments</MenuItem>
            <MenuItem value="backup-data">Create System Backup</MenuItem>
          </TextField>
          
          {selectedAction && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action will affect multiple records. Please confirm before proceeding.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkAction} variant="contained">
            Execute Action
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup & Export Dialog */}
      <Dialog open={backupDialogOpen} onClose={() => setBackupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Backup & Export</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <PictureAsPdf color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Full report (PDF)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Everything in one document: members, every payment, penalties, expenses, metal,
                  loans, distribution draws, meetings and attendance, rules, recent activity and control
                  totals. Keep a copy somewhere safe - if data is ever lost, it shows exactly where
                  the group stood.
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="contained"
              startIcon={reportLoading ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdf />}
              onClick={handleDownloadReport}
              disabled={reportLoading}
              sx={{ mt: 2 }}
            >
              {reportLoading ? 'Preparing the report...' : 'Download full report'}
            </Button>
          </Box>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Or export a single table as a CSV file:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportData('payments')}
                sx={{ mb: 1 }}
              >
                Export Payments
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportData('penalties')}
                sx={{ mb: 1 }}
              >
                Export Penalties
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportData('users')}
                sx={{ mb: 1 }}
              >
                Export Users
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportData('meetings')}
                sx={{ mb: 1 }}
              >
                Export Meetings
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportData('expenses')}
                sx={{ mb: 1 }}
              >
                Export Expenses
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;